/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/no-multi-comp */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Box,
  Chip,
  DialogContent,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { DialogDefaultV2 } from '../../../../components/v2';
import BaseService from '../../../../services/BaseService';
import { formatPeriodoCurto } from '../Operador/constants';
import { TIPO_LABEL, tipoChipSx } from '../../Calendario/dateUtils';

const uniqueByIdOrKey = (items) => {
  const seen = new Set();
  return (items || []).filter((f) => {
    const key = f.id || `${f.data}|${f.nome}|${f.tipo}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const FeriadoRow = ({ item }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      py: 1,
      borderBottom: 1,
      borderColor: 'divider',
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={600} noWrap>
        {item.nome}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {moment(item.data).format('DD/MM/YYYY (ddd)')}
        {item.municipioNome ? ` · ${item.municipioNome}` : ''}
      </Typography>
    </Box>
    <Chip
      size="small"
      label={TIPO_LABEL[item.tipo] || item.tipo}
      sx={{
        ...tipoChipSx(item.tipo),
        height: 22,
        fontSize: '0.65rem',
        flexShrink: 0,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  </Box>
);

FeriadoRow.propTypes = {
  item: PropTypes.object.isRequired,
};

const DiasUteisCicloQuickModal = ({
  open,
  onClose,
  inicio,
  fim,
  diasUteis,
  diasUteisGlobais,
  diasUteisRestantes,
  folgas,
}) => {
  const [feriados, setFeriados] = useState([]);
  const [municipioLabel, setMunicipioLabel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const periodoLabel = useMemo(
    () => formatPeriodoCurto(inicio, fim),
    [inicio, fim],
  );

  const loadFeriadosCiclo = useCallback(async () => {
    if (inicio == null || fim == null) {
      setFeriados([]);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const municipioRes = await BaseService.get({ url: 'calendario/municipio-usuario' });
      const municipio = municipioRes?.data;
      const codigoIbge = municipio?.codigoIbge ? String(municipio.codigoIbge) : null;
      if (municipio?.municipioNome) {
        setMunicipioLabel(
          municipio.uf
            ? `${municipio.municipioNome} / ${municipio.uf}`
            : municipio.municipioNome,
        );
      } else {
        setMunicipioLabel(null);
      }

      if (!codigoIbge) {
        setFeriados([]);
        return;
      }

      const start = moment(inicio).startOf('day');
      const end = moment(fim).endOf('day');
      const years = Array.from(new Set([start.year(), end.year()]));
      const lists = await Promise.all(
        years.map(async (ano) => {
          const res = await BaseService.get({
            url: `calendario/${ano}?codigoIbge=${codigoIbge}`,
          });
          return Array.isArray(res?.data) ? res.data : [];
        }),
      );

      const inCycle = uniqueByIdOrKey(lists.flat()).filter((f) => {
        if ((f.status || 'VALIDO') !== 'VALIDO') return false;
        const d = moment(f.data);
        return d.isSameOrAfter(start, 'day') && d.isSameOrBefore(end, 'day');
      });

      inCycle.sort((a, b) => moment(a.data).valueOf() - moment(b.data).valueOf());
      setFeriados(inCycle);
    } catch {
      setFeriados([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [inicio, fim]);

  useEffect(() => {
    if (open) loadFeriadosCiclo();
  }, [open, loadFeriadosCiclo]);

  const subtitleParts = [
    periodoLabel || null,
    diasUteis != null && diasUteisGlobais != null
      ? `${diasUteis}/${diasUteisGlobais}`
      : diasUteis != null
        ? `${diasUteis} no ciclo`
        : null,
    diasUteisRestantes != null ? `${diasUteisRestantes} restantes` : null,
  ].filter(Boolean);

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={onClose}
      title="Dias úteis do ciclo"
      confirmMessage="Fechar"
      readOnly
      maxWidth="sm"
      fullWidth
    >
      <DialogContent>
        <Stack spacing={2}>
          <Box>
            {subtitleParts.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {subtitleParts.join(' · ')}
              </Typography>
            )}
            {municipioLabel && (
              <Typography variant="caption" color="text.secondary">
                Município: {municipioLabel}
              </Typography>
            )}
          </Box>

          <Typography variant="caption" color="text.secondary">
            Fins de semana não contam como dia útil
          </Typography>

          {Array.isArray(folgas) && folgas.length > 0 && (
            <Stack spacing={0}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Folgas pessoais
              </Typography>
              {folgas.map((f) => (
                <FeriadoRow
                  key={f.id || `${f.data}|${f.nome}|${f.tipo}`}
                  item={f}
                />
              ))}
            </Stack>
          )}

          {loading ? (
            <Typography variant="body2" color="text.secondary">Carregando feriados…</Typography>
          ) : error ? (
            <Typography variant="body2" color="text.secondary">
              Não foi possível carregar os feriados do ciclo.
            </Typography>
          ) : !feriados.length ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum feriado no intervalo deste ciclo
              {!municipioLabel ? ' (município do perfil não identificado).' : '.'}
            </Typography>
          ) : (
            <Stack spacing={0}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                Feriados no ciclo
              </Typography>
              {feriados.map((f) => (
                <FeriadoRow
                  key={f.id || `${f.data}|${f.nome}|${f.tipo}`}
                  item={f}
                />
              ))}
            </Stack>
          )}

          <Link
            href="/v2/calendario"
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            underline="hover"
            fontWeight={600}
          >
            Abrir calendário completo
          </Link>
        </Stack>
      </DialogContent>
    </DialogDefaultV2>
  );
};

DiasUteisCicloQuickModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  inicio: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fim: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  diasUteis: PropTypes.number,
  diasUteisGlobais: PropTypes.number,
  diasUteisRestantes: PropTypes.number,
  folgas: PropTypes.arrayOf(PropTypes.object),
};

DiasUteisCicloQuickModal.defaultProps = {
  inicio: null,
  fim: null,
  diasUteis: null,
  diasUteisGlobais: null,
  diasUteisRestantes: null,
  folgas: [],
};

export default DiasUteisCicloQuickModal;
